import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/kit/Button';
import { sameValues } from '@/kit/hooks/useDirtyForm';
import { Icon } from '@/kit/Icon';
import { LeaveGuard } from '@/kit/LeaveGuard';
import { SectionTabs } from '@/kit/SectionTabs';
import { Select } from '@/kit/Select';
import { Toggle } from '@/kit/Toggle';
import { openDialog } from '@/kit/dialog';
import {
  ReportNoteSection,
  type CompanyReportPickItem,
  type DirectorsReportSignatories,
  type NoteEditorDialogData,
  type NoteEditorDialogResult,
  type ReportNoteTextRow,
} from '@/core/models/report-settings.models';
import { capiumTagGroupsFor } from '@/core/models/capium-tags';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { reportSettingsService } from '@/core/services/reportSettings';
import { PrinciplesActivitesDialog } from './PrinciplesActivitesDialog';
import { SETTINGS_ROUTE, useReportClient } from './common';
import { StickyReportSettingsFrame } from './StickyReportSettingsFrame';
import { ClientKind, useClientKind } from './clientKind';
import './DirectorReport.scss';

interface DirectorNoteItem {
  note: ReportNoteTextRow;
  name: string;
  checked: boolean;
}

const emptySignatories = (clientId: string): DirectorsReportSignatories => ({
  clientId,
  reportSignatoryId: null,
  reportSecondSignatoryId: null,
  balanceSheetSignatoryId: null,
  balanceSheetSecondSignatoryId: null,
});

/** The four choices a save sends: a second signatory that is not shown counts as none. */
type SignatoryChoice = Omit<DirectorsReportSignatories, 'clientId'>;

const choiceOf = (s: DirectorsReportSignatories, showReport: boolean, showBalanceSheet: boolean): SignatoryChoice => ({
  reportSignatoryId: s.reportSignatoryId ?? null,
  reportSecondSignatoryId: showReport ? (s.reportSecondSignatoryId ?? null) : null,
  balanceSheetSignatoryId: s.balanceSheetSignatoryId ?? null,
  balanceSheetSecondSignatoryId: showBalanceSheet ? (s.balanceSheetSecondSignatoryId ?? null) : null,
});

const toItem = (note: ReportNoteTextRow): DirectorNoteItem => ({ note, name: note.name, checked: note.isEnabled });

/**
 * Report Settings > Director's Report: up to two signatories for the report and for the balance sheet and the related note
 * paragraphs (each tick or edit saves at once). 3 Nov: the signatories are always editable; Save and Discard are in the
 * sticky page header once a signatory differs from what was loaded or saved, not at the foot of the tab.
 */
export function DirectorReport() {
  const navigate = useNavigate();
  const client = useReportClient();
  const clientType = useClientKind(client?.id)?.clientType;
  const isLlp = clientType === ClientKind.Llp;
  const [showReportRow, setShowReportRow] = useState(false);
  const [showBalanceSheet, setShowBalanceSheet] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [directors, setDirectors] = useState<CompanyReportPickItem[]>([]);
  const [signatories, setSignatories] = useState<DirectorsReportSignatories>(() => emptySignatories(''));
  const [notes, setNotes] = useState<DirectorNoteItem[]>([]);
  const [subnotes, setSubnotes] = useState<DirectorNoteItem[]>([]);
  /** The signatories as last loaded or saved: what the page is compared with, and what Discard puts back. */
  const [baseline, setBaseline] = useState<SignatoryChoice | null>(null);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const applyNotes = (rows: ReportNoteTextRow[]) => {
    setNotes(rows.filter((n) => n.groupNo === 1).map(toItem));
    setSubnotes(rows.filter((n) => n.groupNo === 2).map(toItem));
  };

  useEffect(() => {
    if (!client) return;
    reportSettingsService.getDirectorsReport(client.id).then(
      (view) => {
        if (!alive.current) return;
        setDirectors(view.directors);
        const loaded = { ...emptySignatories(client.id), ...view.signatories };
        setSignatories(loaded);
        setShowReportRow(!!loaded.reportSecondSignatoryId);
        setShowBalanceSheet(!!loaded.balanceSheetSecondSignatoryId);
        setBaseline(choiceOf(loaded, !!loaded.reportSecondSignatoryId, !!loaded.balanceSheetSecondSignatoryId));
        applyNotes(view.notes);
      },
      (error) => alive.current && notifyError(error),
    );
    // Loads once, like ngOnInit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const patchSignatories = (patch: Partial<DirectorsReportSignatories>) =>
    setSignatories((current) => ({ ...current, ...patch }));

  const dirty =
    !!baseline && !sameValues(choiceOf(signatories, showReportRow, showBalanceSheet), baseline);

  const discard = () => {
    if (!baseline || !client || isSaving) return;
    setSignatories({ ...emptySignatories(client.id), ...baseline });
    setShowReportRow(!!baseline.reportSecondSignatoryId);
    setShowBalanceSheet(!!baseline.balanceSheetSecondSignatoryId);
  };

  const removeReportRow = () => {
    setShowReportRow(false);
    patchSignatories({ reportSecondSignatoryId: null });
  };

  const removeBalanceSheetRow = () => {
    setShowBalanceSheet(false);
    patchSignatories({ balanceSheetSecondSignatoryId: null });
  };

  const saveSignatories = () => {
    if (!client) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    setIsSaving(true);
    reportSettingsService
      .saveSignatories({ clientId: client.id, ...choiceOf(signatories, showReportRow, showBalanceSheet) })
      .then(
        (saved) => {
          if (!alive.current) return;
          setSignatories({ ...emptySignatories(client.id), ...saved });
          setShowReportRow(!!saved.reportSecondSignatoryId);
          setShowBalanceSheet(!!saved.balanceSheetSecondSignatoryId);
          setBaseline(choiceOf(saved, !!saved.reportSecondSignatoryId, !!saved.balanceSheetSecondSignatoryId));
          notifySuccess('Director information saved successfully.');
        },
        (error) => alive.current && notifyError(error),
      )
      .finally(() => alive.current && setIsSaving(false));
  };

  const setChecked = (noteKey: string, checked: boolean) => {
    const apply = (list: DirectorNoteItem[]) =>
      list.map((item) => (item.note.noteKey === noteKey ? { ...item, checked } : item));
    setNotes(apply);
    setSubnotes(apply);
  };

  const toggleNote = (item: DirectorNoteItem, checked: boolean) => {
    if (!client) return;
    setChecked(item.note.noteKey, checked);
    reportSettingsService
      .saveReportNoteTexts(client.id, ReportNoteSection.DirectorsReport, [
        { noteKey: item.note.noteKey, isEnabled: checked, setText: false },
      ])
      .then(
        (rows) => alive.current && applyNotes(rows),
        (error) => {
          if (!alive.current) return;
          setChecked(item.note.noteKey, !checked);
          notifyError(error);
        },
      );
  };

  const activitesDialog = (item: DirectorNoteItem) => {
    if (!client) return;
    const data: NoteEditorDialogData = {
      heading: item.name,
      title: item.note.title,
      content: item.note.content,
      defaultTitle: item.note.defaultTitle,
      defaultContent: item.note.defaultContent,
      tagGroups: capiumTagGroupsFor(clientType),
    };
    openDialog<NoteEditorDialogData, NoteEditorDialogResult>(PrinciplesActivitesDialog, { disableClose: true, data })
      .afterClosed()
      .then((result) => {
        if (!result) return;
        reportSettingsService
          .saveReportNoteTexts(client.id, ReportNoteSection.DirectorsReport, [
            {
              noteKey: item.note.noteKey,
              isEnabled: item.checked,
              setText: true,
              title: result.title,
              content: result.content,
            },
          ])
          .then(
            (rows) => {
              if (!alive.current) return;
              applyNotes(rows);
              notifySuccess('Note saved successfully.');
            },
            (error) => alive.current && notifyError(error),
          );
      });
  };

  const signatorySelect = (value: string | null, onChange: (value: string | null) => void) => (
    <Select
      className="w-300"
      items={directors}
      bindLabel="name"
      bindValue="id"
      value={value}
      onChange={(next) => onChange(next as string | null)}
    />
  );

  const noteRow = (item: DirectorNoteItem) => (
    <div key={item.note.noteKey} className="d-flex align-items-center gap-5">
      <Toggle checked={item.checked} onCheckedChange={(checked) => toggleNote(item, checked)}>
        {item.name}
      </Toggle>
      {item.checked && <Icon name="edit" outlined className="info-icon" onClick={() => activitesDialog(item)} />}
    </div>
  );

  return (
    <StickyReportSettingsFrame
      title={isLlp ? 'Members’ Report' : 'Director’s Report'}
      hostClassName="director-report-host"
      onClose={() => navigate(SETTINGS_ROUTE)}
      dirty={dirty}
      busy={isSaving}
      onSave={saveSignatories}
      onDiscard={discard}
    >
      <LeaveGuard when={dirty} />
      {/* Design review 2 Nov 2026: the signatories and the related notes are two tabs, not two columns. */}
      <div className="sidenav-body">
        <SectionTabs
          ariaLabel="Director’s Report sections"
          tabs={[
            { id: 'signatories', label: 'Signature & Approval' },
            { id: 'notes', label: 'Related Notes' },
          ]}
        >
          {(active) =>
            active === 'signatories' ? (
              <div>
                <div className="form-field-title pt-1">
                  <p className="pb-1">Signature & Approval</p>
                </div>
                <div className="form-field">
                  {/* Oct-10 #168: an LLP signs its Members' Report. */}
                  <p>{isLlp ? 'Signatory (Members’ Report)' : 'Signatory (Director’s Report)'}</p>
                  <div className="d-flex align-items-center gap-10">
                    {signatorySelect(signatories.reportSignatoryId, (value) =>
                      patchSignatories({ reportSignatoryId: value }),
                    )}
                    <Button className="light-bg" onClick={() => setShowReportRow(true)}>
                      <Icon name="add" />
                    </Button>
                  </div>
                  {showReportRow && (
                    <div className="d-flex align-items-center gap-10 mt-10">
                      {signatorySelect(signatories.reportSecondSignatoryId, (value) =>
                        patchSignatories({ reportSecondSignatoryId: value }),
                      )}
                      <Button className="light-bg" title="Remove" onClick={removeReportRow}>
                        <Icon name="delete_outline" outlined />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="form-field">
                  <p>Signatory (Balance Sheet)</p>
                  <div className="d-flex align-items-center gap-10">
                    {signatorySelect(signatories.balanceSheetSignatoryId, (value) =>
                      patchSignatories({ balanceSheetSignatoryId: value }),
                    )}
                    <Button className="light-bg" onClick={() => setShowBalanceSheet(true)}>
                      <Icon name="add" />
                    </Button>
                  </div>
                  {showBalanceSheet && (
                    <div className="d-flex align-items-center gap-10 mt-10">
                      {signatorySelect(signatories.balanceSheetSecondSignatoryId, (value) =>
                        patchSignatories({ balanceSheetSecondSignatoryId: value }),
                      )}
                      <Button className="light-bg" title="Remove" onClick={removeBalanceSheetRow}>
                        <Icon name="delete_outline" outlined />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="form-field-title pb-10 pt-1 pt-1">
                <p className="pb-5">Related Notes</p>
                {notes.map(noteRow)}
                <div className="hr-top pt-5 mt-5"></div>
                {subnotes.map(noteRow)}
              </div>
            )
          }
        </SectionTabs>
      </div>
    </StickyReportSettingsFrame>
  );
}
